import { useState, useEffect, useRef } from 'react';
import {
  Navigation,
  Scrollbar,
  Mousewheel,
  Thumbs,
  FreeMode,
  Keyboard,
  Zoom,
} from 'swiper/modules';
import { Swiper, SwiperSlide, SwiperClass } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import 'swiper/css/mousewheel';
import 'swiper/css/thumbs';
import 'swiper/css/free-mode';
import 'swiper/css/keyboard';
import 'swiper/css/zoom';

import {
  ChevronForwardOutlineIc,
  ChevronBackOutlineIc,
  PlayCircleOutlineIc,
  Svg360OutlineIc,
  CloseOutlineIc,
} from '@dsch/dd-icons';

import { refreshAdUnits } from '../../../../../components/Toolkit/DFP/DFP';
import { useGalleryModal } from '../../../../../components/Toolkit/Gallery/components/GalleryModal/GalleryModal.hook';

import {
  Next,
  Previous,
} from '../../../../../components/Toolkit/Gallery/components/GalleryUiElements';
import { LiteYouTubeEmbed } from '../../../../../components/Toolkit/Gallery/components/LiteYouTubeEmbed';

import { AD_DETAILS_DFP_CONFIG } from '../../../../../configs/dfpConfig';
import {
  CDN_STATIC_ASSETS,
  DFP_PHOTOBROWSER_REFRESH_THRESHOLD,
} from '../../../../../utils';
import { disableScroll, enableScroll } from '../../../../../helpers/scroll';

import * as GalleryModalStyled from '../../../../../components/Toolkit/Gallery/components/GalleryModal/GalleryModal.styled';
import * as GalleryViewStyled from '../../../../../components/Toolkit/Gallery/components/GalleryView/GalleryView.styled';

import type { GalleryModalProps } from '../../../../../components/Toolkit/Gallery/components/GalleryModal/GalleryView.typed';

const GalleryModal = ({
  isOpen,
  askToClose,
  testId = '',
  photos,
  link360,
  link360Interior,
  videoConfig,
  renderShareAd,
  renderSaveAd,
  renderDfpUnit,
  initialMediaIndex = 0,
  sidebar = true,
  editing = false,
  renderCounter,
  refreshTopAndSideDFPUnit,
  media: defaultMedia,
}: GalleryModalProps) => {
  const swiperRef = useRef<SwiperClass>();
  const [imageCounterForDFP, setImageCounterForDFP] = useState<number>(0);

  const {
    media,
    thumbsSwiper,
    above1024,
    currentMediaIndex,
    handleCloseRequest,
    setCurrentMediaIndex,
    setThumbsSwiper,
  } = useGalleryModal({
    media: defaultMedia,
    askToClose,
    photos,
    link360,
    link360Interior,
    videoConfig,
    isOpen,
    initialMediaIndex,
    imageCounterForDFP,
    setImageCounterForDFP,
    refreshTopAndSideDFPUnit,
  });

  useEffect(() => {
    if (
      imageCounterForDFP ===
      (parseInt(DFP_PHOTOBROWSER_REFRESH_THRESHOLD) ?? 20)
    ) {
      refreshAdUnits([AD_DETAILS_DFP_CONFIG.dd_detail_photobrowser.slotId]);
      setImageCounterForDFP(0);
    }
  }, [imageCounterForDFP]);

  return (
    <GalleryModalStyled.SModal
      isOpen={isOpen}
      onRequestClose={handleCloseRequest}
      style={{
        overlay: {
          zIndex: 9900,
          backgroundColor: editing ? 'white' : 'transparent',
        },
      }}
      onAfterOpen={disableScroll}
      onAfterClose={enableScroll}
    >
      <GalleryModalStyled.Container data-testid={testId} sidebar={sidebar}>
        {!above1024 && renderDfpUnit && (
          <GalleryModalStyled.TopBanner>
            {renderDfpUnit}
          </GalleryModalStyled.TopBanner>
        )}
        <GalleryModalStyled.Header
          totalMedia={media.length}
          currentCountPosition={currentMediaIndex + 1}
          askToClose={handleCloseRequest}
          renderShareAd={renderShareAd}
          renderSaveAd={renderSaveAd}
          renderCounter={renderCounter}
        />
        <GalleryModalStyled.Body>
          <GalleryModalStyled.MainViewWrapper>
            <GalleryModalStyled.Count>
              {renderCounter
                ? renderCounter(currentMediaIndex + 1, media.length)
                : `${currentMediaIndex + 1} / ${media.length}`}
            </GalleryModalStyled.Count>
            <Previous
              data-testid="main-view-prev-button"
              className={'main-view-prev-button'}
            >
              <ChevronBackOutlineIc width="36" height="36" />
            </Previous>
            <Swiper
              zoom={true}
              preventInteractionOnTransition={true}
              thumbs={{ swiper: thumbsSwiper }}
              onSwiper={(swiper: SwiperClass) => {
                swiperRef.current = swiper;
                setThumbsSwiper(swiper);
              }}
              loop={true}
              height={'100%'}
              spaceBetween={10}
              modules={[Zoom, Keyboard, Navigation, Thumbs, FreeMode]}
              initialSlide={currentMediaIndex}
              tabIndex={currentMediaIndex}
              normalizeSlideIndex={currentMediaIndex}
              onSlideChange={(a: SwiperClass) => {
                setCurrentMediaIndex(a.realIndex);
              }}
              onRealIndexChange={() => {
                setImageCounterForDFP(imageCounterForDFP + 1);
              }}
              keyboard={{
                enabled: true,
              }}
              className="swiper-main"
              navigation={{
                prevEl: '.main-view-prev-button',
                nextEl: '.main-view-next-button',
              }}
              $lazy={true}
            >
              {media.length > 0 &&
                media.map((mediaItem, index) => {
                  if (mediaItem.isPhoto) {
                    return (
                      <SwiperSlide zoom={true} key={index} defaultValue={index}>
                        <GalleryModalStyled.Img
                          src={mediaItem.src}
                          alt={mediaItem.alt}
                          loading="lazy"
                        />
                        <GalleryModalStyled.ImageLoader
                          className="swiper-lazy-preloader"
                          size={'LARGE'}
                          cdnUrl={CDN_STATIC_ASSETS}
                        />
                      </SwiperSlide>
                    );
                  } else if (
                    mediaItem.isVideo &&
                    mediaItem.id &&
                    !mediaItem.isIframe
                  ) {
                    return (
                      <SwiperSlide key={index} defaultValue={index}>
                        <LiteYouTubeEmbed
                          key={index}
                          id={mediaItem.id}
                          adNetwork={false}
                          playlist={false}
                          title="YouTube Embed"
                          noCookie={true}
                          poster="maxresdefault"
                          wrapperClass={`yt-lite youtube ${
                            mediaItem.extendClass ?? ''
                          }`}
                          cdnUrl={CDN_STATIC_ASSETS}
                        />
                      </SwiperSlide>
                    );
                  } else if (mediaItem.is360) {
                    return (
                      <SwiperSlide key={index} defaultValue={index}>
                        <div className={`threeSixtyVewArea`}>
                          <div className={`wrapper-360`}>
                            <iframe
                              width="100%"
                              height="100%"
                              allowFullScreen
                              src={mediaItem.id}
                              className={`fix-360-height`}
                              title="360 View of product"
                              frameBorder="0"
                            />
                          </div>
                        </div>
                      </SwiperSlide>
                    );
                  } else if (mediaItem.isIframe) {
                    return (
                      <SwiperSlide key={index} defaultValue={index}>
                        <div className={`wrapper-360-video`}>
                          <iframe
                            src={mediaItem.src}
                            allowFullScreen={true}
                            scrolling="no"
                            frameBorder="0"
                            className={`fix-360-video-height`}
                          />
                        </div>
                      </SwiperSlide>
                    );
                  }
                })}
            </Swiper>
            <Next
              data-testid="main-view-next-button"
              className={'main-view-next-button'}
            >
              <ChevronForwardOutlineIc width="36" height="36" />
            </Next>
            <GalleryModalStyled.Close
              data-testid="gallery-close"
              onClick={handleCloseRequest}
            >
              <CloseOutlineIc width="20" height="20" />
            </GalleryModalStyled.Close>
          </GalleryModalStyled.MainViewWrapper>

          {sidebar && (
            <GalleryModalStyled.SideBanner>
              {above1024 && renderDfpUnit}
            </GalleryModalStyled.SideBanner>
          )}
        </GalleryModalStyled.Body>
        <GalleryModalStyled.Footer>
          <GalleryModalStyled.CarouselWrapper>
            <GalleryModalStyled.CarouselArrow>
              <Previous
                className={'gallery-prev-button'}
                data-testid="gallery-prev-button"
              >
                <ChevronBackOutlineIc width="36" height="36" />
              </Previous>
            </GalleryModalStyled.CarouselArrow>
            <GalleryModalStyled.CarouselContent>
              <Swiper
                onSwiper={setThumbsSwiper}
                spaceBetween={8}
                breakpoints={{
                  0: {
                    spaceBetween: 4,
                  },
                  640: {
                    spaceBetween: 8,
                  },
                }}
                slidesPerView={'auto'}
                loop={false}
                watchSlidesProgress={true}
                direction={'horizontal'}
                scrollbar={false}
                mousewheel={true}
                modules={[Scrollbar, Mousewheel, Navigation, Thumbs]}
                className="swiper-carousel"
                navigation={{
                  prevEl: '.gallery-prev-button',
                  nextEl: '.gallery-next-button',
                }}
                $lazy={true}
              >
                {media.length > 0 &&
                  media.map((mediaItem, index) => {
                    return (
                      <SwiperSlide
                        key={index}
                        data-tracking="gallery-thumbnail-click"
                      >
                        {mediaItem.isVideo && !mediaItem.isIframe && (
                          <>
                            <GalleryViewStyled.OverlayContent opacity={true}>
                              <PlayCircleOutlineIc width="40" height="40" />
                            </GalleryViewStyled.OverlayContent>
                            <GalleryViewStyled.OverlayImage
                              src={mediaItem.src}
                            />
                          </>
                        )}
                        {mediaItem.is360 && (
                          <>
                            <GalleryViewStyled.OverlayContent opacity={true}>
                              <Svg360OutlineIc width="40" height="40" />
                            </GalleryViewStyled.OverlayContent>
                            <GalleryViewStyled.OverlayImage
                              src={mediaItem.src}
                            />
                          </>
                        )}
                        {mediaItem.isPhoto && (
                          <>
                            <GalleryViewStyled.OverlayImage
                              src={mediaItem.src}
                              loading="lazy"
                            />
                            <GalleryModalStyled.ImageLoader
                              className="swiper-lazy-preloader"
                              size={'DEFAULT'}
                              cdnUrl={CDN_STATIC_ASSETS}
                            />
                          </>
                        )}
                        {mediaItem.isIframe && (
                          <>
                            <GalleryViewStyled.OverlayContent opacity={true}>
                              <PlayCircleOutlineIc width="40" height="40" />
                            </GalleryViewStyled.OverlayContent>
                            <GalleryViewStyled.OverlayImage
                              src={`${CDN_STATIC_ASSETS}/images/fallbacks/thumbnail-360.webp`}
                              loading="lazy"
                            />
                          </>
                        )}
                      </SwiperSlide>
                    );
                  })}
              </Swiper>
            </GalleryModalStyled.CarouselContent>
            <GalleryModalStyled.CarouselArrow>
              <Next
                data-testid="gallery-next-button"
                className={'gallery-next-button'}
              >
                <ChevronForwardOutlineIc width="36" height="36" />
              </Next>
            </GalleryModalStyled.CarouselArrow>
          </GalleryModalStyled.CarouselWrapper>
        </GalleryModalStyled.Footer>
      </GalleryModalStyled.Container>
    </GalleryModalStyled.SModal>
  );
};

export { GalleryModal };
